import { ApolloClient, HttpLink, InMemoryCache } from '@apollo/client';

import { AppEndpointConfig } from '~/contexts/app-config';
import { GetServiceNoticeDocument, LanguageCode } from '~/types';

/**
 * A service notice as shown to the member. The title and message are localized by the
 * server, so they are displayed as-is. `validUntil` is an ISO date time string (UTC);
 * the notice is only valid until that moment.
 */
export type ServiceNotice = {
    title: string;
    message: string;
    moreInfoUrl: string | null;
    validUntil: string;
};

/**
 * True when the given value is an http(s) URL. The notice's more-info link is
 * server-controlled, but the UI only ever renders or opens http(s) URLs so that an
 * unexpected or malformed scheme (e.g. `javascript:`) can never reach the linking APIs.
 */
export function isHttpUrl(url: string | null | undefined): url is string {
    return !!url && (url.startsWith('https://') || url.startsWith('http://'));
}

/**
 * The service notice is checked with its own bare Apollo client (no auth, no caching
 * of previous results between checks), so that the check works before sign-in and
 * independently of the main app client.
 */
export const createServiceNoticeClient = ({ http, host }: AppEndpointConfig) => {
    return new ApolloClient({
        link: new HttpLink({ uri: `${http}://${host}/member-api/graphql/` }),
        cache: new InMemoryCache()
    });
};

/**
 * Thrown when the notice could not be asked from the server at all: no answer, or an answer
 * saying the server itself could not serve the request. This is what a switched-off backend
 * looks like, and it is the only failure the app's built-in fallback end date reacts to
 * (see service-notice-fallback.ts).
 */
export class ServiceNoticeUnreachableError extends Error {
    constructor(message: string) {
        super(message);
        this.name = 'ServiceNoticeUnreachableError';
    }
}

/**
 * True when the failure came from the API itself answering. GraphQL errors, and HTTP statuses
 * below 500, mean the request did reach a working service, so the service is alive even though
 * this particular query failed. Statuses of 500 and above mean the server could not serve the
 * request, which is treated the same as no answer at all.
 *
 * The check is on the error's own properties rather than Apollo's error classes, so it also
 * works where '@apollo/client' is mocked in tests. Apollo sets `name` on CombinedGraphQLErrors
 * and CombinedProtocolErrors, and `statusCode` on ServerError and ServerParseError.
 */
function serverAnswered(error: unknown): boolean {
    if (!error || typeof error !== 'object') {
        return false;
    }

    const { name } = error as Error;
    if (name === 'CombinedGraphQLErrors' || name === 'CombinedProtocolErrors') {
        return true;
    }

    const { statusCode } = error as { statusCode?: unknown };

    return typeof statusCode === 'number' && statusCode < 500;
}

/**
 * Fetches the current service notice from the server.
 * Returns null when no notice is active (the normal state).
 *
 * Throws ServiceNoticeUnreachableError when the server could not be asked, and a plain Error
 * when the service answered but the query still failed. The caller relies on that difference:
 * only "could not be asked" may lead to the app treating the service as closed.
 */
export async function fetchServiceNotice(client: ApolloClient, language: LanguageCode): Promise<ServiceNotice | null> {
    let data;

    try {
        ({ data } = await client.query({ query: GetServiceNoticeDocument, variables: { language } }));
    } catch (error: unknown) {
        if (serverAnswered(error)) {
            throw new Error(`Service notice query failed: ${error}`);
        }

        throw new ServiceNoticeUnreachableError(`Service notice query failed: ${error}`);
    }

    // Apollo resolved without throwing but gave us nothing usable. Something did answer, so
    // this is not treated as "could not be asked".
    if (!data) {
        throw new Error('Service notice query failed: the response carried no data');
    }

    const notice = data.serviceNotice;
    if (!notice) {
        return null;
    }

    return {
        title: notice.title,
        message: notice.message,
        moreInfoUrl: notice.moreInfoUrl ?? null,
        validUntil: notice.validUntil
    };
}
